import type { TFunction } from 'i18next';
import type { RecurringOccurrences, RecurrenceDaysOfWeekEnum } from '../../../api/generated';
import { WEEKDAYS_IN_ORDER, DEFAULT_INTERVAL_WEEKS } from './bookingConstants';
import { timeWindowParts } from './bookingTime';

const TIME_RANGE_SEPARATOR = ' – ';

// `daysOfWeek` is typed as a Set but arrives as a plain JSON array; Array.from handles both.
const selectedDaysOf = (time: RecurringOccurrences): RecurrenceDaysOfWeekEnum[] =>
  Array.from(time.recurrence?.daysOfWeek ?? []);

/** "Mon, Thu" in Mon–Sun order, plus "every N weeks" when the interval is more than weekly. */
export const formatRecurrencePattern = (t: TFunction, time: RecurringOccurrences): string => {
  const selected = selectedDaysOf(time);
  const days = WEEKDAYS_IN_ORDER.filter((option) => selected.includes(option.day))
    .map((option) => t(option.shortLabelKey))
    .join(', ');
  const intervalWeeks = time.recurrence?.intervalWeeks ?? DEFAULT_INTERVAL_WEEKS;
  return intervalWeeks > DEFAULT_INTERVAL_WEEKS
    ? `${days} · ${t('booking-every-nth-week', { n: intervalWeeks })}`
    : days;
};

/** The series period: "2026-06-16 – 2026-08-30", or "From 2026-06-16" when open-ended. */
export const formatRecurrencePeriod = (t: TFunction, time: RecurringOccurrences): string => {
  const start = time.occurrenceStart ?? '';
  return time.occurrenceEnd
    ? `${start}${TIME_RANGE_SEPARATOR}${time.occurrenceEnd}`
    : t('booking-from-date', { date: start });
};

/** Compact pattern-plus-time summary, e.g. "Mon, Thu · 10:00 – 12:00" or "Mon, Thu · All day". */
export const formatRecurrenceSummary = (t: TFunction, time: RecurringOccurrences): string => {
  const window = timeWindowParts(time.timeWindow);
  const timeLabel = window
    ? `${window.startTime}${TIME_RANGE_SEPARATOR}${window.endTime}`
    : t('booking-all-day');
  return `${formatRecurrencePattern(t, time)} · ${timeLabel}`;
};
